/*eslint-disable*/
import React, { useEffect, useState, useCallback } from 'react'
import { Tabs, Tab, Box } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useSearchParams } from 'react-router-dom'
import customParseFormat from 'dayjs/plugin/customParseFormat'
// components
import Breadcrumbs from '../../components/Breadcrumbs'
import Filters from './components/filters'
import ActivitiesTable from './components/activitiesTable'
import SecurityTable from './components/securityTable'
import TabPanel from '../../components/tabPanel'

// actions
import {
  getUserActivities,
  resetUserActivities,
} from '../../store/reducers/userActivitiesSlice'
import { getUserId } from '../../helpers/user'
import { fetchLogs } from '../../services/user'

// helpers
import { removeEmptyProperties } from '../../helpers/general'
import { extractResources } from './components/securityTable/securityTable.helpers'
import useUser from '../../hooks/useUser'

// constants
import { INITIAL_FILTERS } from './userActivities.constants'

//styles
import styles from './userActivities.module.scss'
import { JsonRenderer } from '@xiam/xiam-ui-toolkit'

const useStyles = makeStyles({
  root: {
    '& .MuiBox-root': {
      padding: '24px 0',
    },
    '@media (max-width: 768px)': {
      overflowY: 'auto',
      scrollbarGutter: 'stable',
    }
  },
})

dayjs.extend(utc)
dayjs.extend(customParseFormat)

function UserActivities() {
  const [isFetching, setIsFetching] = useState(false)
  const [securityFilters, setSecurityFilters] = useState(INITIAL_FILTERS)
  const [queryParams] = useSearchParams()
  const [autoSearch, setAutoSearch] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [selectedTabIndex, setSelectedTabIndex] = useState(0)
  const [securityData, setSecurityData] = useState({
    rows: [],
    searched: false,
    loading: false,
    error: null,
  })

  const [rowData, setRowData] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const handleViewJson = (rowData) => {
    setIsModalOpen(true)
    setRowData(rowData)
  }

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const classes = useStyles()

  const getActivities = async () => {
    setIsFetching(true)
    try {
      if (Object.keys(removeEmptyProperties(filters).length >= 1)) {
        const res = await dispatch(getUserActivities(filters)).unwrap()
        if (res.totalRecordsMatched == 0) {
          toast.warning(t('no-records-found'))
        }
      }
    } catch (error) {
      let message = error.message
      if (error.message.includes('403')) {
        message = t('unauthorized-auditor-access')
      }
      dispatch(resetUserActivities())
      toast.error(message)
    } finally {
      setIsFetching(false)
    }
  }

  const handleUnauthorized = useCallback(() => {
    toast.error(t('unauthorized-auditor-access'))
    dispatch(resetUserActivities())
  }, [dispatch])

  const handleNoRecords = useCallback(() => {
    toast.warning(t('no-records-found'))
  }, [])

  const handleTabChange = (event, index) => {
    setSelectedTabIndex(index)
  }

  const runSecuritySearch = useCallback(async () => {
    const effectiveFilters = removeEmptyProperties(securityFilters)
    if (Object.keys(effectiveFilters).length < 1) return
    
    setSecurityData((s) => ({
      ...s,
      loading: true,
      error: null,
      searched: true,
    }))
    
    try {
      const startTime = dayjs
      .utc(effectiveFilters.startTime)
      .format('YYYY-MM-DDTHH:mm:ss[Z]')
      const endTime = dayjs
      .utc(effectiveFilters.endTime)
      .format('YYYY-MM-DDTHH:mm:ss[Z]')
      const loginOrUserId = (effectiveFilters.email ?? '').trim() || getUserId()

      const res = await fetchLogs(loginOrUserId, startTime, endTime)
      const rows = extractResources(res)

      setSecurityData({ rows, searched: true, loading: false, error: null })
      if (rows.length === 0) handleNoRecords()
    } catch (e) {
      setSecurityData((s) => ({
        ...s,
        rows: [],
        loading: false,
        error: e,
        searched: true,
      }))
      if (String(e?.message || '').includes('403')) handleUnauthorized()
    }
  }, [securityFilters, handleNoRecords, handleUnauthorized])

  useEffect(() => {
    if (Object.keys(removeEmptyProperties(filters)).length >= 1) {
    }
  }, [filters])

  const qEmail = queryParams.get("user-id")
  const { user: userObj, isUserLoading } = useUser(qEmail)
  const paramUserEmail = userObj?.emails?.[0]?.value || ""
  useEffect(()=>{

   const qStartDate = queryParams.get("start-date")
   const qEndDate = queryParams.get("end-date")

   const hasQuery = paramUserEmail || qStartDate || qEndDate

   if (!hasQuery) return

   const updatedFilter = {
     ...INITIAL_FILTERS,
     ...(paramUserEmail && {email: paramUserEmail}),
     ...(qStartDate && {
      startTime: dayjs(qStartDate, 'YYYY-MM-DD')
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(dayjs().second())
     }),
     ...(qEndDate && {
      endTime: dayjs(qEndDate, 'YYYY-MM-DD')
        .hour(dayjs().hour())
        .minute(dayjs().minute())
        .second(dayjs().second())
     }),
   }

   setSecurityFilters(updatedFilter)
   if(paramUserEmail){
     setAutoSearch(true)
   }
   
  }, [userObj])
  

  const breadcrumbPath = [{ label: 'audit-user-log-tab' }]

  return (
    <>
      <div
        className={`${styles.stickyHeader} ${styles.wrapper}`}
      >
        <Breadcrumbs className={styles.stickyHeader} crumbs={breadcrumbPath} />
      </div>
      <Box className={styles.container}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            {t('user-activities-audits-for-single-user')}
          </h1>
          <Tabs
            sx={{ backgroundColor: '#F5F5F5', justifyContent: 'left' }}
            TabIndicatorProps={{
              sx: {
                backgroundColor: '#EB1700',
                height: '4px',
                zIndex: '2',
              },
            }}
            className={styles.tabContainer}
            value={selectedTabIndex}
            onChange={handleTabChange}
          >
            <Tab
              label={t('user-activities-tab-label-security')}
              className={styles.tabLabel}
            />
            <Tab
              label={t('user-activities-tab-label-api')}
              className={styles.tabLabel}
            />
          </Tabs>
        </div>
        <TabPanel
          className={classes.root}
          value={selectedTabIndex}
          index={0}
          sx={{ width: '50%' }}
        >
          <Filters
            filters={securityFilters}
            setFilters={setSecurityFilters}
            getUserActivities={runSecuritySearch}
            variant="security"
            autoSearch={autoSearch}
            setAutoSearch={setAutoSearch}
          />
          <SecurityTable
            rows={securityData.rows}
            loading={securityData.loading}
            searched={securityData.searched}
            onJsonView={handleViewJson}
          />
        </TabPanel>
        <TabPanel
          value={selectedTabIndex}
          index={1}
          sx={{
            width: '50%',
            '@media (max-width: 768px)': {
              overflowY: 'auto',
              scrollbarGutter: 'stable',
            },
           }}
          className={classes.root}
        >
          <Filters
            filters={filters}
            setFilters={setFilters}
            getUserActivities={getActivities}
          />
          <ActivitiesTable onJsonView={handleViewJson} />
        </TabPanel>
        {isModalOpen && (
          <JsonRenderer
            json={rowData}
            onClose={() => {
              setIsModalOpen(false)
            }}
            headerText={t('json-renderer-title-system')}
            viewMode="modal"
          />
        )}
      </Box>
    </>
  )
}

export default UserActivities
